import { useCallback, useEffect, useState } from "react";
import { staffStorySubmissions, staffStoryReview, type StorySubmission } from "../lib/api";
import { useT } from "../i18n";
import { errorMessage } from "../i18n/errors";

// Модерация сторис-заявок (маркетинг на стенде и админ): участник сдал
// инсту → «Засчитать» или «Отклонить». Отклонённый может сдать заново.
export default function StoryReview() {
  const { t } = useT();
  const [subs, setSubs] = useState<StorySubmission[] | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await staffStorySubmissions();
      setSubs(r.submissions);
      setError("");
    } catch (e: unknown) {
      setError(errorMessage(e, t, "admin.task.errLoad"));
    }
  }, [t]);

  useEffect(() => {
    load();
    const timer = setInterval(load, 10000);
    return () => clearInterval(timer);
  }, [load]);

  async function review(phone: string, approve: boolean) {
    setBusy(phone);
    try {
      await staffStoryReview(phone, approve);
      await load();
    } catch (e: unknown) {
      setError(errorMessage(e, t, "admin.task.errSave"));
    } finally {
      setBusy(null);
    }
  }

  if (error && !subs) return <p className="text-red-400 text-sm">{error}</p>;
  if (!subs) return <p className="text-muted text-sm">…</p>;

  const pending = subs.filter((s) => s.status === "pending").length;

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center gap-2.5">
        <h2 className="text-lg font-extrabold">{t("admin.story.title")}</h2>
        {pending > 0 && (
          <span className="text-[12px] font-bold text-gold-light bg-gold/[0.14] border border-gold/40 px-2.5 py-1 rounded-full">
            {t("admin.story.pendingCount", { n: pending })}
          </span>
        )}
      </div>
      {error && <p className="text-sm text-red-400">{error}</p>}
      {subs.length === 0 && <p className="text-muted text-sm">{t("admin.story.empty")}</p>}
      <div className="bg-card border border-line-2 rounded-[18px] divide-y divide-white/5">
        {subs.map((sub) => (
          <div key={sub.phone} className="flex items-center gap-3 p-4 flex-wrap">
            <div className="flex-1 min-w-[180px]">
              <div className="text-sm font-bold text-white">
                {sub.nickname || "—"}
                {sub.clan && <span className="text-muted font-normal"> · {sub.clan}</span>}
              </div>
              <a
                href={`https://instagram.com/${sub.instagram}`}
                target="_blank"
                rel="noreferrer"
                className="text-[13px] text-violet-lighter"
              >
                @{sub.instagram}
              </a>
              <span className="text-muted2 text-xs ml-2">{sub.phone}</span>
            </div>
            {sub.status === "approved" ? (
              <span className="text-[12px] font-bold text-success bg-success/[0.12] px-2.5 py-1 rounded-md">
                {t("admin.story.approved")}
              </span>
            ) : (
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => review(sub.phone, true)}
                  disabled={busy === sub.phone}
                  className="h-9 px-4 rounded-[10px] bg-violet-grad text-white font-bold text-[13px] active:opacity-90 disabled:opacity-60"
                >
                  {t("admin.story.approve")}
                </button>
                <button
                  type="button"
                  onClick={() => review(sub.phone, false)}
                  disabled={busy === sub.phone}
                  className="h-9 px-4 rounded-[10px] bg-white/[0.06] text-white/80 font-bold text-[13px] ring-1 ring-white/10 active:bg-white/10 disabled:opacity-60"
                >
                  {t("admin.story.reject")}
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
